import { Box, SelectChangeEvent, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaDropDown, { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown";
import LoadingPlaceholder from "../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { useAppStore } from "../../../../../hooks/useStores";
import { isLegacyTgRoute } from "../../../utils/tg-utils.ts";
import { TrainingMode } from "../../store/tg-model-store.tsx";
import styles from "./SelectTgDatasetStep.module.scss";

type SelectTgDatasetStepProps = {
	handleSkip: () => void;
	handleSelectDataset: (isSelectExisting: boolean) => void;
};

const SelectTgDatasetStep = ({ handleSkip, handleSelectDataset }: SelectTgDatasetStepProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.steps.selectDataset" });
	const { projectId } = useParams();
	const { tgStore } = useAppStore();
	const { tgProjectStore, tgModelStore, tgDatasetStore } = tgStore;
	const [datasetOptions, setDatasetOptions] = useState<DropDownItem[]>([]);
	const [datasetId, setDatasetId] = useState<number | undefined>(undefined);
	const uploadingDatasetImages =
		tgDatasetStore.loadingUpdateDataset ||
		tgDatasetStore.loadingDuplicateDataset ||
		tgDatasetStore.loadingUploadImages ||
		tgDatasetStore.loadingRegenerate ||
		tgDatasetStore.loadingCreateDataset ||
		tgDatasetStore.loadingGeneratePrefix;

	useEffect(() => {
		const skipSelectDatasetStep = async () => {
			tgDatasetStore.loadingDataset = true;
			if (tgModelStore.modelForm.training_version === "fibo") {
				const duplicatedDataset = await tgDatasetStore.duplicateDataset(tgModelStore.modelForm.dataset_id);
				duplicatedDataset.images = (duplicatedDataset.images ?? []).map((img) => ({
					...img,
					caption: "",
				}));
				tgDatasetStore.datasetForm = duplicatedDataset;
				tgModelStore.modelForm.dataset_id = duplicatedDataset.id;
			} else {
				tgDatasetStore.datasetForm = await tgDatasetStore.getDataset(tgModelStore.modelForm.dataset_id);
			}
			handleSkip();
			tgModelStore.isDatasetFromExisting = true;
			tgModelStore.handleFormChange("dataset_id", tgDatasetStore.datasetForm.id);
		};

		if (tgModelStore.modelForm.dataset_id) {
			skipSelectDatasetStep();
		} else {
			if (!tgProjectStore.paginatedDatasets.items.length) {
				tgProjectStore.paginatedDatasets = { items: [], total: 0 };
				tgProjectStore.getDatasets(Number(projectId), undefined, undefined, 0);
			}
		}
	}, []);

	useEffect(() => {
		const datasets = tgProjectStore.paginatedDatasets.items;
		if (datasets.length) {
			let filteredDatasets = datasets.filter(
				(d) => d.status !== "Failed" && !(d.upload_type === "advanced" && d.status === "Draft"),
			);

			// Show all valid datasets; advanced will be disabled in the dropdown for fully automated mode
			const options = filteredDatasets.map((dataset) => ({
				key: dataset.name,
				value: dataset.id,
				extraData: { uploadType: dataset.upload_type },
			}));

			setDatasetOptions(options);
		}
	}, [tgProjectStore.paginatedDatasets.items, tgModelStore.modelForm.training_mode]);

	const handleDatasetOnChange = async (e: SelectChangeEvent<number>) => {
		if (isLegacyTgRoute()) {
			const datasetId = Number(e.target.value);
			const isAdvanced =
				tgProjectStore.paginatedDatasets.items.find((d) => d.id === datasetId)?.upload_type == "advanced";
			let selectedDataset;
			if (isAdvanced) {
				selectedDataset = await tgDatasetStore.getDataset(datasetId, 6);
			} else {
				selectedDataset = await tgDatasetStore.getDataset(datasetId);
			}
			if (selectedDataset) {
				tgDatasetStore.datasetForm = selectedDataset;
				tgModelStore.isDatasetFromExisting = true;
				tgModelStore.handleFormChange("dataset_id", selectedDataset.id);
				handleSelectDataset(true);
				setDatasetId(selectedDataset.id);
			}
		} else {
			const datasetId = Number(e.target.value);
			const duplicatedDataset = await tgDatasetStore.duplicateDataset(datasetId);
			duplicatedDataset.images = (duplicatedDataset.images ?? []).map((img) => ({
				...img,
				caption: "",
			}));
			tgDatasetStore.datasetForm = duplicatedDataset;
			tgModelStore.modelForm.dataset_id = duplicatedDataset.id;
			tgModelStore.isDatasetFromExisting = true;
			handleSelectDataset(true);
		}
	};

	return (
		<LoadingPlaceholder isLoading={tgDatasetStore.loadingDataset} className={styles.stepWrapper}>
			<Box className={styles.stepWrapper}>
				<Box className={styles.content}>
					<Typography className={styles.header}>{t("header")}</Typography>
					<Typography className={styles.description}>{t("description")}</Typography>
					<BriaButton
						buttonType="primaryMedium"
						onClick={() => handleSelectDataset(false)}
						className={styles.createDatasetButton}
					>
						{t("createDatasetButton")}
					</BriaButton>
					<Typography className={styles.or}>{t("or")}</Typography>
					<BriaDropDown
						placeholder={
							tgProjectStore.loadingProjectDatasets || tgProjectStore.paginatedDatasets.items.length
								? t("dropdown.placeholder")
								: t("dropdown.emptyPlaceholder")
						}
						items={datasetOptions.map((option) => {
							const isAdvanced = option.extraData?.uploadType === "advanced";
							const isDisabled =
								isAdvanced && tgModelStore.modelForm.training_mode === TrainingMode.FULLY_AUTOMATED;
							return {
								...option,
								badge: isAdvanced ? t("dropdown.advancedBadge") : undefined,
								badgeType: isAdvanced ? "secondary" : undefined,
								disabled: isDisabled,
								tooltip: isDisabled ? t("dropdown.advancedDisabledTooltip") : undefined,
							};
						})}
						value={datasetId}
						onChange={handleDatasetOnChange}
						fullWidth
						loading={tgProjectStore.loadingProjectDatasets || tgDatasetStore.loadingDuplicateDataset}
						disabled={
							tgProjectStore.loadingProjectDatasets ||
							tgDatasetStore.loadingDuplicateDataset ||
							uploadingDatasetImages
						}
					/>
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(SelectTgDatasetStep);
